import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { adminHideBrandRating, adminUnhideBrandRating } from '../../api/payment/brand-profile.api';
import { HIDE_REASON_LIMITS, validateHideReason } from '../../utils/brand-profile';
import { apiErrorMessage } from '../../utils/api-error-message';

export interface AdminRatingHideDialogProps {
  ratingId: string;
  mode: 'hide' | 'unhide';
  onClose: () => void;
  onDone: () => void;
}

/** Admin ẩn/bỏ ẩn một đánh giá vi phạm (spec 2026-10-12 §4) — cùng kiểu ô mã Authenticator như modal miễn trừ. */
const AdminRatingHideDialog: React.FC<AdminRatingHideDialogProps> = ({ ratingId, mode, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const hide = mode === 'hide';
  const reasonError = hide ? validateHideReason(reason) : null;

  const submit = async () => {
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    if (reasonError) {
      toast.error(reasonError);
      return;
    }
    setBusy(true);
    try {
      if (hide) await adminHideBrandRating(ratingId, reason.trim(), totp);
      else await adminUnhideBrandRating(ratingId, totp);
      toast.success(hide ? 'Đã ẩn đánh giá.' : 'Đã bỏ ẩn đánh giá.');
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, hide ? 'Ẩn đánh giá thất bại.' : 'Bỏ ẩn đánh giá thất bại.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-[440px] rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800" onClick={(e) => e.stopPropagation()}>
        <h4 className="mb-2 text-lg font-semibold text-slate-800 dark:text-white">{hide ? 'Ẩn đánh giá' : 'Bỏ ẩn đánh giá'}</h4>
        <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          Chỉ ẩn đánh giá xúc phạm, lộ thông tin cá nhân hoặc quảng cáo. Đánh giá bị ẩn không hiện công khai và không tính vào điểm.
        </p>
        {hide && (
          <>
            <label htmlFor="rating-hide-reason" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Lý do ({HIDE_REASON_LIMITS.minLength}–{HIDE_REASON_LIMITS.maxLength} ký tự) <span className="text-red-500">*</span>
            </label>
            <textarea
              id="rating-hide-reason"
              aria-label="Lý do ẩn"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={HIDE_REASON_LIMITS.maxLength}
              rows={3}
              placeholder="VD: nhận xét có số điện thoại riêng của creator."
              className="mb-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
            <div className="mb-4 text-right text-xs text-slate-400">
              {reason.trim().length}/{HIDE_REASON_LIMITS.maxLength}
            </div>
          </>
        )}
        <label htmlFor="rating-hide-totp" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
          Mã Authenticator (6 số) <span className="text-red-500">*</span>
        </label>
        <input
          id="rating-hide-totp"
          aria-label="Mã Authenticator"
          value={totp}
          onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          inputMode="numeric"
          className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-lg tracking-[0.3em] outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
        />
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-10 min-w-[90px] rounded-md border border-gray-300 px-4 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Huỷ
          </button>
          <button
            type="button"
            disabled={busy || totp.length !== 6 || !!reasonError}
            onClick={submit}
            className="h-10 min-w-[130px] rounded-md bg-green-500 px-4 text-sm font-semibold text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Xác nhận
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminRatingHideDialog;
